/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Explore
 *| (C) Copyright IBM Corp. 2018
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */

import { types, getEnv } from 'mobx-state-tree';

export default types
	.model('SelectionModel', {
		items: types.array(types.string)
	})
	.volatile(() => ({
		pivot: null,
		shiftPivot: null
	}))
	.views((self) => ({
		/**
		 * Returns whether there are card(s) selected or not.
		 * @param {string} id - id of card to test whether it is selected
		 * @return {boolean}
		 */
		hasSelections() {
			return self.items.length > 0;
		},
		/**
		 * Determine if a card is selected
		 * @param {string} id - id of card to test whether it is selected
		 * @return {boolean}
		 */
		isSelected(id) {
			return self.items.indexOf(id) > -1;
		},
		get allItems() {
			return getEnv(self).getItems();
		},
		get pivotItem() {
			return self.pivot || (self.allItems.length ? self.allItems[0] : null);
		}
	}))
	.actions((self) => {
		function _select(id) {
			if (self.items.indexOf(id) === -1) {
				self.items.push(id);
			}
		}

		function _defaultSelect(id) {
			self.items = [];
			self.pivot = id;
			self.shiftPivot = null;

			_select(id);
		}

		function _controlSelect(id) {
			// If the item is already selected, deselect it.
			if (self.isSelected(id)) {
				self.deselect(id);
			} else {
				self.pivot = id;
				self.shiftPivot = null;

				_select(id);
			}
		}

		function _shiftSelect(id) {
			const allItems = self.allItems;
			const index = allItems.indexOf(id);

			if (index < 0) {
				throw new Error('Trying to select an item that does not exist.');
			}

			let start, end;
			const pivotIndex = allItems.indexOf(self.pivotItem);

			// Remove indices that are outside of the new shift range, but part of the previous range.
			if (self.shiftPivot) {
				const shiftPivotIndex = allItems.indexOf(self.shiftPivot);

				const deselecting = [];

				if (shiftPivotIndex < pivotIndex) {
					start = shiftPivotIndex;
					end = index > pivotIndex ? pivotIndex : index;
				} else {
					start = index < pivotIndex ? pivotIndex + 1 : index + 1;
					end = shiftPivotIndex + 1;
				}

				for(let i = start; i < end; i++) {
					deselecting.push(allItems[i]);
				}

				deselecting.forEach(id => self.deselect(id));
			}
			self.shiftPivot = id;

			if (index < pivotIndex) {
				start = index + 1;
				end = pivotIndex;
			} else {
				start = pivotIndex + 1;
				end = index;
			}

			// Select the items in the shift range, except for the currently selected item.
			for(let i = start; i < end; i++) {
				if (self.items.indexOf(allItems[i]) === -1) {
					self.items.push(allItems[i]);
				}
			}

			// Select the actual item.
			_select(id);
		}

		return {
			clear() {
				self.items = [];
			},
			selectAll() {
				self.items = self.allItems;
			},
			select(id, { control=false, shift=false } = {}) {
				if (control) {
					_controlSelect(id);
				} else if (shift) {
					_shiftSelect(id);
				} else {
					_defaultSelect(id);
				}
			},
			deselect(id) {
				const index = self.items.indexOf(id);
				if (index >= 0) {
					self.items.splice(index, 1);
				}
			}
		};
	});



// WEBPACK FOOTER //
// ./src/js/layout/explorecontainer/SelectionModel.js